import { useState } from "react";

import { api, ApiError } from "../api/client";
import { ui } from "../lib/ui";
import type { Branch } from "../types";

/** Where one camera's footage comes from: the folder its recordings already
 *  live in on the worker PC (`<folder>/<YYYYMMDD>/*.mp4`, read in place —
 *  PUT/DELETE `/branches/{id}/camera-dirs/{camera id}`), or, unlinked, the
 *  camera's subfolder of each day's footage folder, where files are copied.
 *  The path is the server's to check; nothing is uploaded. */
export function CameraFootageField({
  branch,
  cameraId,
  onSaved,
  onError,
}: {
  branch: Branch;
  cameraId: string;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}) {
  const linked = branch.camera_dirs[cameraId] ?? null;
  const [draft, setDraft] = useState(linked ?? "");
  const [busy, setBusy] = useState(false);
  const trimmed = draft.trim();
  const sep = (linked ?? trimmed).includes("\\") ? "\\" : "/";

  const act = async (action: () => Promise<unknown>, message: string, after?: () => void) => {
    setBusy(true);
    try {
      await action();
      after?.();
      onSaved(message);
    } catch (err) {
      onError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-4 flex flex-col gap-2">
      <div className="flex flex-wrap items-end gap-2">
        <label className={`${ui.field} min-w-[280px] flex-1`}>
          Footage folder on the worker PC (optional)
          <input
            className={`${ui.input} font-mono text-xs`}
            value={draft}
            placeholder="e.g. D:\NVR\cam1"
            onChange={(e) => setDraft(e.target.value)}
          />
        </label>
        <button
          type="button"
          className={ui.btnQuiet}
          disabled={busy || !trimmed || trimmed === linked}
          onClick={() =>
            act(
              () => api.putCameraDir(branch.id, cameraId, trimmed),
              `Camera "${cameraId}" linked to ${trimmed} — runs read its footage there`,
            )
          }
        >
          {linked ? "Change folder" : "Link folder"}
        </button>
        {linked && (
          <button
            type="button"
            className={ui.btnQuiet}
            disabled={busy}
            onClick={() =>
              act(
                () => api.deleteCameraDir(branch.id, cameraId),
                `Camera "${cameraId}" unlinked — its footage is copied into the day folder again`,
                () => setDraft(""),
              )
            }
          >
            Unlink
          </button>
        )}
      </div>
      <p className="m-0 text-xs text-muted">
        {linked ? (
          <>
            Runs read this camera's recordings in place from{" "}
            <span className="font-mono">
              {linked}
              {sep}&lt;YYYYMMDD&gt;{sep}
            </span>{" "}
            — nothing is copied, and nothing is written there. A day with no
            folder there falls back to the camera's subfolder of the day folder.
          </>
        ) : (
          <>
            The folder holding this camera's recordings, one{" "}
            <span className="font-mono">YYYYMMDD</span> folder per day, so runs
            read them in place. Without one, copy each day's files into{" "}
            <span className="font-mono">
              &lt;footage dir&gt;/{branch.id}/&lt;YYYY-MM-DD&gt;/{cameraId}/
            </span>
            .
          </>
        )}
      </p>
    </div>
  );
}
